/* eslint-disable dot-notation */
import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { Conditional, Space, Text, Button } from '@cashfree-intl/coherent';

import { TEST, PROD } from '../../../utils/constants';
import { getItemFromLocalStorage } from '../../../utils/common';

import {
  START,
  FIRST,
  SECOND,
  COMPLETED,
  PAYTM_REPORT_GENERATED,
  CONNECT_PAYTM_ACCOUNT_SUCCESS,
} from '../constants';

import CustomIcon from '../../../components/CustomIcons';

function PaytmTransactions({
  data,
  getPaytmCheckSumHash,
  resetEditMode,
  openModal,
  closeModal,
  testLoader,
  resetTestLoader,
  resetShowPaytmForm,
  accountStatus,
}) {
  const { paytmTestConfig, paytmProdConfig } = data;
  const { stage } = paytmTestConfig;

  const testTransactions = {
    [TEST]: [
      {
        key: `${TEST}_1`,
        text: 'Test Transaction 1',
        checked: stage === START,
        success: stage !== START,
      },
      {
        key: `${TEST}_2`,
        text: 'Test Transaction 2',
        checked: stage === FIRST,
        success: stage === SECOND || stage === COMPLETED,
      },
      {
        key: `${TEST}_3`,
        text: 'Test Transaction 3',
        checked: stage === SECOND,
        success: stage === COMPLETED,
      },
    ],
    [PROD]: [
      {
        key: PROD,
        text: 'Test Transaction',
      },
    ],
  };

  const testTransaction = params => {
    const form = document.createElement('form');
    const actionUrl = isEmpty(paytmProdConfig)
      ? process.env.TEST_PAYTM_TXN_URL
      : process.env.PROD_PAYTM_TXN_URL;

    form.setAttribute('method', 'post');
    form.setAttribute('target', '_blank');
    form.setAttribute('action', actionUrl);
    form.setAttribute('rel', 'opener');

    Object.keys(params).forEach(key => {
      const input = document.createElement('input');

      input.setAttribute('type', 'hidden');
      input.setAttribute('name', key);
      input.setAttribute('value', params[key]);

      form.appendChild(input);
    });

    document.getElementById('jwt-auth-form').appendChild(form);

    form.submit();

    document.getElementById('jwt-auth-form').removeChild(form);
  };

  const onTestPaytmTransaction = obj => {
    const envObj = process.env;

    const callbackUrl =
      envObj.API_URL +
      (isEmpty(paytmProdConfig)
        ? envObj.TEST_PAYTM_CALLBACK_URL
        : envObj.PROD_PAYTM_CALLBACK_URL);

    const paytmParams = {
      MID: obj.merchantPaytmId,
      WEBSITE: obj.merchantPaytmWebsiteUrl,
      CHANNEL_ID: obj.merchantPaytmChannelId,
      INDUSTRY_TYPE_ID: obj.merchantPaytmIndustryType,
      ORDER_ID: `${obj.merchantId}_${Date.now()}`,
      CUST_ID: String(obj.merchantId),
      TXN_AMOUNT: '1',
      CALLBACK_URL: callbackUrl,
      EMAIL: getItemFromLocalStorage('email'),
    };

    resetTestLoader(true);

    getPaytmCheckSumHash({
      paytmParams,
      merchantPaytmKey: obj.merchantPaytmKey,
      cb: testTransaction,
    });
  };

  const handleUpdateMID = () => {
    resetShowPaytmForm(true);
    resetEditMode(true);
    resetTestLoader(false);
  };

  const renderTestTransaction = () => (
    <Space
      className={
        isEmpty(paytmProdConfig) ? 'test-transaction' : 'test-transaction-prod'
      }
      direction="column"
    >
      {testTransactions[isEmpty(paytmProdConfig) ? TEST : PROD].map(
        (obj, index) => (
          <Space key={obj.key}>
            <Conditional if={obj.key !== PROD}>
              <div className={`vertical-line ${index !== 2 ? 'active' : ''}`}>
                <Conditional if={obj.success}>
                  <CustomIcon
                    style={{ position: 'absolute', left: '-0.495rem' }}
                    componentName="GREEN_TICK"
                  />
                </Conditional>
                <Conditional if={obj.checked}>
                  <CustomIcon
                    style={{ position: 'absolute', left: '-0.595rem' }}
                    componentName="RADIO"
                  />
                </Conditional>
                <Conditional if={!obj.checked && !obj.success}>
                  <CustomIcon
                    style={{ position: 'absolute', left: '-0.495rem' }}
                    componentName="ELLIPSE"
                  />
                </Conditional>
              </div>
            </Conditional>
            <Space className="test-item" direction="column">
              <Text
                variant="b14"
                color={
                  obj.key === PROD || obj.checked || obj.success
                    ? 'body'
                    : 'placeholder'
                }
                className={obj.key === PROD || obj.checked ? 'mr-btm-8' : ''}
                strong
              >
                {obj.text}
              </Text>
              <Conditional if={obj.key === PROD || obj.checked}>
                <Text
                  className="mr-btm-32 w-500"
                  variant="b12"
                  color="bodyLight"
                >
                  Complete the test transaction for ₹ 1 and mark it successful
                  so that we can verify your{' '}
                  {obj.key === PROD ? 'Production' : 'Test'} API Credentials.
                </Text>
                <Space>
                  <Button
                    secondary
                    size="small"
                    className="mr-right-8"
                    onClick={() =>
                      onTestPaytmTransaction(
                        obj.key === PROD ? paytmProdConfig : paytmTestConfig,
                      )
                    }
                    loading={testLoader}
                    disabled={testLoader}
                  >
                    <Text variant="b12" strong color="primary">
                      Initiate Transaction
                    </Text>
                  </Button>
                  <Conditional if={obj.key === `${TEST}_1`}>
                    <Button size="small" onClick={handleUpdateMID} link>
                      <Text variant="b12" strong color="primary">
                        Update Merchant ID
                      </Text>
                    </Button>
                  </Conditional>
                </Space>
              </Conditional>
            </Space>
          </Space>
        ),
      )}
    </Space>
  );

  return (
    <Space direction="column">
      <Space>
        <Space className="content-left" direction="column">
          <Text variant="b14" color="bodyLight" strong>
            {isEmpty(paytmProdConfig) ? 'Test' : 'Production'} API Credentials
          </Text>
          <Text variant="b14" color="body" className="active" strong>
            Test Transactions
          </Text>
        </Space>
        <Space direction="column">
          {isEmpty(paytmProdConfig) ? (
            <Text className="description" variant="b14" color="bodyLight">
              Please perform all three test transactions to generate a
              transaction report to validate the linking of your Cashfree
              account with your PayTM wallet. You will be directed to
              PayTM&apos;s site to complete the transactions, please mark each a
              success.
            </Text>
          ) : (
            <Text className="description" variant="b14" color="bodyLight">
              Perform the below transaction to validate your production
              credential.
            </Text>
          )}
          {renderTestTransaction()}
        </Space>
      </Space>
      <Space
        className="cta-wrapper"
        justifyContent="flex-end"
        alignItems="center"
      >
        <Button link onClick={closeModal}>
          Cancel
        </Button>
        <Button
          primary
          disabled={
            isEmpty(paytmProdConfig)
              ? stage !== COMPLETED
              : accountStatus !== 'ACTIVE'
          }
          onClick={() =>
            openModal(
              isEmpty(paytmProdConfig)
                ? PAYTM_REPORT_GENERATED
                : CONNECT_PAYTM_ACCOUNT_SUCCESS,
            )
          }
        >
          {stage === COMPLETED ? 'Confirm' : 'Next'}
        </Button>
      </Space>
    </Space>
  );
}

PaytmTransactions.propTypes = {
  data: PropTypes.object.isRequired,
  getPaytmCheckSumHash: PropTypes.func.isRequired,
  resetEditMode: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  testLoader: PropTypes.bool.isRequired,
  resetTestLoader: PropTypes.func.isRequired,
  resetShowPaytmForm: PropTypes.func.isRequired,
  accountStatus: PropTypes.string.isRequired,
};

export default memo(PaytmTransactions);
